import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    HierarchicalTableProps,
    PendingChange,
    HandlingTierData,
    ProductionCapabilityData,
    ActionType
} from './types';
import { ProductionCapabilityRow } from './ProductionCapabilityRow';
import { HandlingTierRow } from './HandlingTierRow';
import { TableControls, FilterOptions, SortOption } from './TableControls';
import { DEFAULT_WHT, DEFAULT_WHTS } from 'src/types/WarehouseHandlingTier';
import {
    registerAddedTier,
    isTierAdded,
    getAddedTiers,
    logTierRegistry,
    TIER_ADDED_EVENT,
    getRegistryVersion,
    forceRegistryUpdate
} from './tierRegistry';
import './styles.css';
import { PC_ADDED_EVENT } from './GSLAHierarchicalTableAdapter';

/**
 * Main component for the hierarchical table that displays GSLA data
 * with handling tiers nested under production capabilities.
 */
export const HierarchicalTable: React.FC<HierarchicalTableProps> = ({
    data,
    onSave,
    userCanEdit,
    initialPendingChanges = {},
    warehouseId,
    availableTiers,
    isPSSD
}) => {
    // State for tracking which production capabilities are expanded
    // Initialize with some rows expanded by default for demonstration
    // Listen for registry updates to force table re-renders
    const [registryUpdateCounter, setRegistryUpdateCounter] = useState(0);
    
    // State for tracking which production capabilities are expanded
    const [expandedPCs, setExpandedPCs] = useState<Record<string, boolean>>(() => {
        const initialExpanded: Record<string, boolean> = {};
        // Auto-expand all PCs by default to show their child tier rows
        data.productionCapabilities.forEach(pc => {
            initialExpanded[pc.id] = true;
        });
        return initialExpanded;
    });
    
    useEffect(() => {
        const handleTierAdded = (event: Event) => {
            // Force re-render when any tier is added
            setTimeout(() => {
                setRegistryUpdateCounter(prev => prev + 1);
                
                // Also force all getAvailableTiersForPC calls to re-evaluate
                forceRegistryUpdate();
            }, 0);
        };
        
        const handlePCAdded = (event: CustomEvent) => {
            // Auto-expand a newly added PC so users can see the default tier
            const { pcName } = event.detail;
            setExpandedPCs(prev => ({
                ...prev,
                [pcName]: true // Set the newly added PC to expanded state
            }));
        };
        
        // Add event listeners
        window.addEventListener(TIER_ADDED_EVENT, handleTierAdded);
        window.addEventListener(PC_ADDED_EVENT, handlePCAdded as EventListener);
        
        // Clean up
        return () => {
            window.removeEventListener(TIER_ADDED_EVENT, handleTierAdded);
            window.removeEventListener(PC_ADDED_EVENT, handlePCAdded as EventListener);
        };
    }, []); // Only add listeners once
    
    // Local state for the table data so we can modify it for visualization
    const [tableData, setTableData] = useState<HierarchicalTableProps['data']>(data);
    
    // Sync tableData with data prop when it changes
    useEffect(() => {
        setTableData(data);
        
        // Clean up pending changes that reference non-existent PCs or tiers
        setPendingChanges(prevChanges => {
            const newChanges: Record<string, Record<string, PendingChange>> = {};
            
            Object.entries(prevChanges).forEach(([pcId, pcChanges]) => {
                const pc = data.productionCapabilities.find(p => p.id === pcId);
                if (!pc) {
                    // PC no longer exists, skip its changes
                    return;
                }
                
                const validTierChanges: Record<string, PendingChange> = {};
                Object.entries(pcChanges).forEach(([tierId, change]) => {
                    // Check if tier still exists using unified approach
                    const tierExists = findTierById(pc, tierId) !== undefined;
                    
                    if (tierExists) {
                        validTierChanges[tierId] = change;
                    }
                });
                
                if (Object.keys(validTierChanges).length > 0) {
                    newChanges[pcId] = validTierChanges;
                }
            });
            
            return newChanges;
        });
        
        // Update expanded state for new PCs and remove deleted ones
        setExpandedPCs(prevExpanded => {
            const newExpanded: Record<string, boolean> = {};
            
            // Keep expansion state for existing PCs
            data.productionCapabilities.forEach(pc => {
                if (prevExpanded[pc.id] !== undefined) {
                    newExpanded[pc.id] = prevExpanded[pc.id];
                } else {
                    // Auto-expand new PCs
                    newExpanded[pc.id] = true;
                }
            });
            
            return newExpanded;
        });
        
        // Reset validation state
        setInvalidRows(new Set());
    }, [data]);
    
    // State for pending changes to GSLA values
    const [pendingChanges, setPendingChanges] = useState<Record<string, Record<string, PendingChange>>>(initialPendingChanges);
    
    // Sync pending changes when initialPendingChanges changes
    useEffect(() => {
        setPendingChanges(initialPendingChanges);
    }, [initialPendingChanges]);
    
    // Remove the circular update - parent manages pending changes, child just receives them
    // No need to notify parent of changes since parent is the source of truth

    // State for validation tracking
    const [invalidRows, setInvalidRows] = useState<Set<string>>(new Set());
    
    // State for filters and sorting
    const [filters, setFilters] = useState<FilterOptions>({
        productionCapability: '',
        handlingTier: '',
        status: '',
        recommendation: ''
    });
    const [sorting, setSorting] = useState<SortOption>({
        field: 'pc',
        direction: 'asc'
    });

    // Toggle expansion state for a production capability
    const handleToggleExpand = useCallback((pcId: string) => {
        setExpandedPCs(prev => ({
            ...prev,
            [pcId]: !prev[pcId]
        }));
    }, []);

    // Helper functions to treat all tiers uniformly
    const getAllTiers = useCallback((pc: ProductionCapabilityData): HandlingTierData[] => {
        return [pc.defaultHandlingTier, ...pc.additionalHandlingTiers];
    }, []);

    const findTierById = useCallback((pc: ProductionCapabilityData, tierId: string): HandlingTierData | undefined => {
        return getAllTiers(pc).find(tier => tier.id === tierId);
    }, [getAllTiers]);

    const isDefaultTier = useCallback((pc: ProductionCapabilityData, tierId: string): boolean => {
        return pc.defaultHandlingTier.id === tierId;
    }, []);

    // Check if a change is valid
    const isChangeValid = useCallback((change: PendingChange | undefined): boolean => {
        if (!change) return true; // No change is valid
        
        // "NoAction" is always valid
        if (change.action === ActionType.NO_ACTION) return true;
        
        // "Recommended" is valid if we have a recommended value
        if (change.action === ActionType.RECOMMENDED) return true;
        
        // "Delete" is always valid
        if (change.action === ActionType.DELETE) return true;
        
        // For other actions, we need a valid GSLA value
        return change.newGSLA !== undefined && change.newGSLA > 0;
    }, []);

    // Update invalid rows whenever pendingChanges changes
    useEffect(() => {
        const newInvalidRows = new Set<string>();
        
        Object.entries(pendingChanges).forEach(([pcId, tierChanges]) => {
            Object.entries(tierChanges).forEach(([tierId, change]) => {
                if (!isChangeValid(change)) {
                    newInvalidRows.add(`${pcId}_${tierId}`);
                }
            });
        });
        
        setInvalidRows(newInvalidRows);
    }, [pendingChanges, isChangeValid]);

    // Handle value changes (recommended or override)
    const handleValueChange = useCallback((pcId: string, tierId: string, value: number, action: string) => {
        setPendingChanges(prev => {
            // Get the PC this tier belongs to
            const pc = tableData.productionCapabilities.find(p => p.id === pcId);
            if (!pc) return prev;
            
            // Find the tier using unified approach
            const tier = findTierById(pc, tierId);
            if (!tier) return prev;

            // Create nested objects if they don't exist
            const pcChanges = prev[pcId] || {};
            
            // If action is NoAction, remove the pending change
            if (action === ActionType.NO_ACTION) {
                const newPcChanges = { ...pcChanges };
                delete newPcChanges[tierId];
                
                // If no more changes for this PC, remove the PC entry
                if (Object.keys(newPcChanges).length === 0) {
                    const newChanges = { ...prev };
                    delete newChanges[pcId];
                    return newChanges;
                }
                
                return {
                    ...prev,
                    [pcId]: newPcChanges
                };
            }
            
            // Otherwise add/update the pending change
            return {
                ...prev,
                [pcId]: {
                    ...pcChanges,
                    [tierId]: {
                        productionCapability: pc.name,
                        handlingTier: tier.name,
                        currentGSLA: tier.currentGSLA,
                        newGSLA: value,
                        action,
                        isDefault: tier.isDefault
                    }
                }
            };
        });
    }, [tableData]);

    // Version counter to force re-renders when tier selection changes
    const [tierVersionCounter, setTierVersionCounter] = useState<number>(0);
    
    // Handle adding a new handling tier to a PC
    const handleAddTier = useCallback((pcId: string, tierName: string) => {
        // First, verify we're not already trying to add a duplicate
        const existingTier = tableData.productionCapabilities
            .find(pc => pc.id === pcId)?.additionalHandlingTiers
            .find(t => t.name === tierName);
            
        if (existingTier) {
            return;  // Exit early, no need to update any state
        }
        
        // Use the tierRegistry to track this tier
        registerAddedTier(pcId, tierName);
        
        // Log the current registry state (no-op in production)
        logTierRegistry();
        
        // Increment version counter to force re-renders of dependent components
        setTierVersionCounter(prev => prev + 1);
        
        // For visualization purposes, we'll actually add the tier to our local state
        setTableData(prevData => {
            // Create a new copy of the data
            const newData = { ...prevData };
            
            // Find the PC to add the tier to
            const pcIndex = newData.productionCapabilities.findIndex(pc => pc.id === pcId);
            if (pcIndex === -1) return prevData;
            
            // Get the PC
            const pc = newData.productionCapabilities[pcIndex];
            
            // Create a new handling tier with a unique ID to prevent React key collisions
            // This ensures we don't have duplicate React keys even if a tier is somehow added twice
            const uniqueSuffix = `_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
            const newTier: HandlingTierData = {
                id: `${pcId}_${tierName.toLowerCase().replace(/\s+/g, '_')}${uniqueSuffix}`,
                name: tierName,
                currentGSLA: undefined, // New tiers don't have a current GSLA value
                recommendedGSLA: undefined, // New tiers don't have a recommended GSLA value
                publishDateEpochMillis: undefined, // No publish date as it's never been published
                recommendationDateEpochMillis: undefined, // No recommendation date
                isDefault: false
            };
            
            // Create a new PC with the new tier added
            const updatedPC: ProductionCapabilityData = {
                ...pc,
                additionalHandlingTiers: [...pc.additionalHandlingTiers, newTier]
            };
            
            // Replace the PC in the array
            newData.productionCapabilities[pcIndex] = updatedPC;
            
            return newData;
        });
    }, [tableData]);

    // Handle deleting a handling tier from a PC
    const handleDeleteTier = useCallback((pcId: string, tierId: string) => {
        // For visualization purposes, we'll actually remove the tier from our local state
        setTableData(prevData => {
            // Create a new copy of the data
            const newData = { ...prevData };
            
            // Find the PC to remove the tier from
            const pcIndex = newData.productionCapabilities.findIndex(pc => pc.id === pcId);
            if (pcIndex === -1) return prevData;
            
            // Get the PC
            const pc = newData.productionCapabilities[pcIndex];
            
            // Find the tier to remove
            const tierIndex = pc.additionalHandlingTiers.findIndex(t => t.id === tierId);
            if (tierIndex === -1) return prevData;
            
            // Create a new PC with the tier removed
            const updatedPC: ProductionCapabilityData = {
                ...pc,
                additionalHandlingTiers: pc.additionalHandlingTiers.filter(t => t.id !== tierId)
            };
            
            // Replace the PC in the array
            newData.productionCapabilities[pcIndex] = updatedPC;
            
            return newData;
        });
        
        // Also remove any pending changes for this tier
        setPendingChanges(prev => {
            if (!prev[pcId] || !prev[pcId][tierId]) return prev;
            
            const newPcChanges = { ...prev[pcId] };
            delete newPcChanges[tierId];
            
            // If no more changes for this PC, remove the PC entry
            if (Object.keys(newPcChanges).length === 0) {
                const newChanges = { ...prev };
                delete newChanges[pcId];
                return newChanges;
            }
            
            return {
                ...prev,
                [pcId]: newPcChanges
            };
        });
    }, []);

    // Get a list of available tiers that can be added to a PC
    const getAvailableTiersForPC = useCallback((pcId: string) => {
        // Use passed availableTiers prop (country-filtered WHTs)
        const standardTiers: string[] = availableTiers!;
        
        // Find the PC
        const pc = tableData.productionCapabilities.find(p => p.id === pcId);
        if (!pc) {
            return [];
        }
        
        // Create a Set of tier names that are already used by this PC for efficient lookups
        const usedTiers = new Set<string>();
        
        // Only consider the default tier as "used" if it has actual data or a pending change
        const defaultTierHasData = pc.defaultHandlingTier.currentGSLA !== undefined ||
                                   pc.defaultHandlingTier.recommendedGSLA !== undefined;
        const defaultTierHasPendingChange = pendingChanges[pc.id] &&
                                           pendingChanges[pc.id][pc.defaultHandlingTier.id];
        
        if (defaultTierHasData || defaultTierHasPendingChange) {
            usedTiers.add(pc.defaultHandlingTier.name);
        }
        
        // Add all additional handling tiers
        pc.additionalHandlingTiers.forEach(t => {
            usedTiers.add(t.name);
        });
        
        // Get added tiers from the global registry
        const globallyAddedTiers = getAddedTiers(pcId);
        globallyAddedTiers.forEach(tier => {
            usedTiers.add(tier);
        });
        
        // Return only the tiers that aren't already used or being added
        return standardTiers.filter(tier => {
            const isUsed = usedTiers.has(tier);
            const isGloballyAdded = isTierAdded(pcId, tier);
            return !isUsed && !isGloballyAdded;
        });
    }, [tableData, tierVersionCounter, pendingChanges]); // Include tierVersionCounter and pendingChanges to force re-evaluation

    // Handle save action
    const handleSave = useCallback(async () => {
        // Check for invalid rows
        if (invalidRows.size > 0) {
            return;
        }
        
        // Flatten the pending changes into a single array
        const changes: PendingChange[] = [];
        
        // Process changes PC by PC to ensure consistent behavior with default tiers
        Object.entries(pendingChanges).forEach(([pcId, pcChanges]) => {
            const pc = tableData.productionCapabilities.find(p => p.id === pcId);
            if (!pc) return;
            
            // Check if the default tier is being deleted
            const isDefaultTierDeleted = pcChanges[pc.defaultHandlingTier.id]?.action === ActionType.DELETE;
            
            // Process each tier change
            Object.entries(pcChanges).forEach(([tierId, change]) => {
                // If this PC's default tier is marked for deletion, ensure all tiers are marked for deletion
                // On PSSD sites, only delete the default tier — the PC can exist without it
                if (isDefaultTierDeleted && !isPSSD && change.action !== ActionType.DELETE) {
                    changes.push({
                        ...change,
                        action: ActionType.DELETE
                    });
                } else {
                    changes.push(change);
                }
            });
        });
        
        // Call the onSave prop with the flattened changes
        await onSave(changes);
        
        // Clear the pending changes
        setPendingChanges({});
    }, [pendingChanges, onSave, invalidRows, tableData]);

    // Count the number of pending changes
    const pendingChangeCount = Object.values(pendingChanges)
        .reduce((count, pcChanges) => count + Object.keys(pcChanges).length, 0);

    // Check if save button should be disabled
    const isSaveDisabled = invalidRows.size > 0 || pendingChangeCount === 0;
    
    // Extract all production capability names and handling tier names for filters
    const availablePCs = useMemo(() => {
        const pcNames = tableData.productionCapabilities.map(pc => pc.name);
        // Use Array.from instead of spread to avoid TS errors with downlevel iteration
        return Array.from(new Set(pcNames));
    }, [tableData]);
    
    const availableTiersForFilter = useMemo(() => {
        // Start with the default tier
        const tiers = new Set<string>([DEFAULT_WHT]);
        
        // Add all handling tiers from all PCs
        tableData.productionCapabilities.forEach(pc => {
            pc.additionalHandlingTiers.forEach(tier => {
                tiers.add(tier.name);
            });
        });
        
        // Use Array.from instead of spread to avoid TS errors with downlevel iteration
        return Array.from(tiers);
    }, [tableData]);
    
    // Handle filter changes
    const handleFilterChange = useCallback((newFilters: FilterOptions) => {
        setFilters(newFilters);
    }, []);
    
    // Handle sort changes
    const handleSortChange = useCallback((newSorting: SortOption) => {
        setSorting(newSorting);
    }, []);
    
    // Apply filters and sorting to the data
    const filteredAndSortedPCs = useMemo(() => {
        let filtered = [...tableData.productionCapabilities];
        
        // Apply filters
        if (filters.productionCapability) {
            filtered = filtered.filter(pc => 
                pc.name.toLowerCase().includes(filters.productionCapability.toLowerCase())
            );
        }
        
        if (filters.handlingTier) {
            filtered = filtered.filter(pc => {
                // Check if the tier exists in this PC (either as default or additional)
                if (pc.defaultHandlingTier.name === filters.handlingTier) {
                    return true;
                }
                
                return pc.additionalHandlingTiers.some(tier => 
                    tier.name === filters.handlingTier
                );
            });
        }
        
        if (filters.status) {
            filtered = filtered.filter(pc => {
                const pcChanges = pendingChanges[pc.id];
                
                if (filters.status === 'unchanged' && !pcChanges) {
                    return true;
                }
                
                if (filters.status === 'recommendation-accepted') {
                    return Object.values(pcChanges || {}).some(change => 
                        change.action === 'Recommended'
                    );
                }
                
                if (filters.status === 'override-pending') {
                    return Object.values(pcChanges || {}).some(change => 
                        change.action !== 'NoAction' && change.action !== 'Recommended'
                    );
                }
                
                return true;
            });
        }
        
        if (filters.recommendation) {
            filtered = filtered.filter(pc => {
                if (filters.recommendation === 'higher' && 
                    pc.defaultHandlingTier.recommendedGSLA !== undefined && 
                    pc.defaultHandlingTier.currentGSLA !== undefined &&
                    pc.defaultHandlingTier.recommendedGSLA > pc.defaultHandlingTier.currentGSLA) {
                    return true;
                }
                
                if (filters.recommendation === 'lower' && 
                    pc.defaultHandlingTier.recommendedGSLA !== undefined && 
                    pc.defaultHandlingTier.currentGSLA !== undefined &&
                    pc.defaultHandlingTier.recommendedGSLA < pc.defaultHandlingTier.currentGSLA) {
                    return true;
                }
                
                if (filters.recommendation === 'none' && 
                    pc.defaultHandlingTier.recommendedGSLA === undefined) {
                    return true;
                }
                
                return false;
            });
        }
        
        // Apply sorting
        return filtered.sort((a, b) => {
            let valA: any;
            let valB: any;
            
            switch (sorting.field) {
                case 'pc':
                    valA = a.name;
                    valB = b.name;
                    break;
                case 'tier':
                    // Sort by default tier name (not very useful but included for completeness)
                    valA = a.defaultHandlingTier.name;
                    valB = b.defaultHandlingTier.name;
                    break;
                case 'current':
                    valA = a.defaultHandlingTier.currentGSLA || 0;
                    valB = b.defaultHandlingTier.currentGSLA || 0;
                    break;
                case 'recommended':
                    valA = a.defaultHandlingTier.recommendedGSLA || 0;
                    valB = b.defaultHandlingTier.recommendedGSLA || 0;
                    break;
                case 'modified':
                    valA = a.defaultHandlingTier.publishDateEpochMillis || 0;
                    valB = b.defaultHandlingTier.publishDateEpochMillis || 0;
                    break;
                case 'recommended-date':
                    valA = a.defaultHandlingTier.recommendationDateEpochMillis || 0;
                    valB = b.defaultHandlingTier.recommendationDateEpochMillis || 0;
                    break;
                default:
                    valA = a.name;
                    valB = b.name;
            }
            
            // Handle string comparison
            if (typeof valA === 'string' && typeof valB === 'string') {
                const comparison = valA.localeCompare(valB);
                return sorting.direction === 'asc' ? comparison : -comparison;
            }
            
            // Handle number comparison
            const comparison = valA - valB;
            return sorting.direction === 'asc' ? comparison : -comparison;
        });
    }, [tableData, filters, sorting, pendingChanges]);

    return (
        <div className="hierarchical-table-container">
            {/* Filter and Sort Controls */}
            <TableControls 
                availablePCs={availablePCs}
                availableTiers={availableTiersForFilter}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
            />
            
            {/* Table legend */}
            <div className="table-legend">
                <div className="legend-item">
                    <span className="higher-recommendation">▲ Higher</span>
                    <span> (potentially slower) / </span>
                    <span className="lower-recommendation">▼ Lower</span>
                    <span> (potentially faster) Recommended GSLA</span>
                </div>
                <div className="legend-item">
                    <div className="legend-color recommendation-accepted"></div>
                    <span>Recommendation Accepted</span>
                </div>
                {userCanEdit && (
                    <>
                        <div className="legend-item">
                            <div className="legend-color override-pending"></div>
                            <span>Override Pending</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color delete-pending"></div>
                            <span>Delete Pending</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color parent-delete-pending"></div>
                            <span>Parent PC Deleted</span>
                        </div>
                    </>
                )}
            </div>

            <div className="table-instructions">
                <p><strong>Note:</strong> Click the "Add Handling Tier" button or expand icon (▶) to manage handling tiers for a production capability.</p>
                {userCanEdit && (
                    <p>To override a value, enter a new value AND select a specific reason. Both are required for the override to be valid.</p>
                )}
            </div>

            <table className="hierarchical-table">
                <thead>
                    <tr>
                        <th className="expand-header"></th>
                        <th>Production Capability</th>
                        <th>Handling Tier</th>
                        <th>Current GSLA</th>
                        <th>Last Modified</th>
                        <th>Recommended GSLA</th>
                        <th>Recommendation Date</th>
                        {userCanEdit && (
                            <>
                                <th>Override</th>
                                <th>Action</th>
                            </>
                        )}
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredAndSortedPCs.map(pc => {
                        const isExpanded = !!expandedPCs[pc.id];
                        const pcPendingChanges = pendingChanges[pc.id] || {};
                        const hasAdditionalTiers = pc.additionalHandlingTiers.length > 0;
                        
                        return (
                            <React.Fragment key={pc.id}>
                                {/* Production Capability Row */}
                                <ProductionCapabilityRow
                                    pc={pc}
                                    isExpanded={isExpanded}
                                    onToggleExpand={() => handleToggleExpand(pc.id)}
                                    pendingChanges={new Map(Object.entries(pcPendingChanges))}
                                    onValueChange={handleValueChange}
                                    onDelete={userCanEdit && !isPSSD ? () => {} : undefined} // On PSSD, default tier deletion is handled at the tier-row level
                                    onAddHandlingTier={userCanEdit ? handleAddTier : undefined}
                                    availableTiers={getAvailableTiersForPC(pc.id)}
                                    userCanEdit={userCanEdit}
                                    warehouseId={warehouseId}
                                    isPSSD={isPSSD}
                                />
                                
                                {/* Handling Tier Rows - only shown when expanded */}
                                {isExpanded && (() => {
                                    // Only render the default tier as a child row if it has meaningful data
                                    const rows = [];
                                    
                                    // Check if the default tier should be rendered
                                    // It should be rendered if it has data OR if there's a pending change for it
                                    const shouldRenderDefaultTier =
                                        pc.defaultHandlingTier.currentGSLA !== undefined ||
                                        pc.defaultHandlingTier.recommendedGSLA !== undefined ||
                                        pcPendingChanges[pc.defaultHandlingTier.id] !== undefined;
                                    
                                    if (shouldRenderDefaultTier) {
                                        rows.push(
                                            <HandlingTierRow
                                                key={pc.defaultHandlingTier.id}
                                                tier={pc.defaultHandlingTier}
                                                pcId={pc.id}
                                                pcName={pc.name}
                                                pendingChange={pcPendingChanges[pc.defaultHandlingTier.id]}
                                                parentPcDeletePending={false}
                                                onValueChange={handleValueChange}
                                                onDelete={isPSSD ? handleDeleteTier : undefined}
                                                userCanEdit={userCanEdit}
                                                warehouseId={warehouseId}
                                            />
                                        );
                                    }
                                    
                                    // Then process additional tiers
                                    // De-duplicate tiers by name to prevent duplicate UI entries
                                    const uniqueTiers = new Map<string, HandlingTierData>();
                                    pc.additionalHandlingTiers.forEach(tier => {
                                        // Only add the tier if we haven't seen its name before
                                        if (!uniqueTiers.has(tier.name)) {
                                            uniqueTiers.set(tier.name, tier);
                                        }
                                    });
                                    
                                    // Convert to array and map to components
                                    const additionalTierRows = Array.from(uniqueTiers.values()).map(tier => (
                                        <HandlingTierRow
                                            key={tier.id}
                                            tier={tier}
                                            pcId={pc.id}
                                            pcName={pc.name}
                                            pendingChange={pcPendingChanges[tier.id]}
                                            parentPcDeletePending={!isPSSD && pcPendingChanges[pc.defaultHandlingTier.id]?.action === ActionType.DELETE}
                                            onValueChange={handleValueChange}
                                            onDelete={handleDeleteTier}
                                            userCanEdit={userCanEdit}
                                            warehouseId={warehouseId}
                                        />
                                    ));
                                    
                                    return [...rows, ...additionalTierRows];
                                })()}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            
            {/* Save Changes Button */}
            {userCanEdit && pendingChangeCount > 0 && (
                <div className="save-changes-container">
                    <button 
                        className="save-changes-button"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        title={invalidRows.size > 0 ? "Complete all validation requirements before saving" : ""}
                    >
                        Save {pendingChangeCount} {pendingChangeCount === 1 ? 'Change' : 'Changes'}
                        {invalidRows.size > 0 && (
                            <span> (fix validation issues first)</span>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};
